import { ReactNode, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'next-i18next';
import BathroomIcon from '../../../../new-components/svg-icons/BathroomIcon';
import NewAreaIcon from '../../../../new-components/svg-icons/NewAreaIcon';
import BedsIcon from '../../../../new-components/svg-icons/BedsIcon';

interface AmenityProps {
  icon: ReactNode | JSX.Element;
  label: string;
  value: number | string;
}

interface CardAmenitiesProps {
  bedrooms: number;
  bathrooms: number;
  min_area: number;
}

const Amenity = ({ icon, value, label }: AmenityProps) => {
  const { t } = useTranslation('common');
  return (
    <AmenityContainer>
      {icon}
      <span className="value">{value}</span>
      <span className="label">{t(label)}</span>
    </AmenityContainer>
  );
};
const CardAmenities = ({
  bedrooms,
  bathrooms,
  min_area
}: CardAmenitiesProps) => {
  const generateAmenities = useCallback(() => {
    const amenities = [];

    if (min_area > 0) {
      amenities.push({ icon: <NewAreaIcon />, value: min_area, label: 'm2' });
    }

    if (bedrooms > 0) {
      amenities.push({ icon: <BedsIcon />, value: bedrooms, label: 'beds' });
    }

    if (bathrooms > 0) {
      amenities.push({
        icon: <BathroomIcon />,
        value: bathrooms,
        label: 'card_baths'
      });
    }

    return amenities;
  }, [bedrooms, bathrooms, min_area]);
  return (
    <Container>
      {generateAmenities().map(amenity => (
        <Amenity
          key={amenity.label}
          icon={amenity.icon}
          value={amenity.value}
          label={amenity.label}
        />
      ))}
    </Container>
  );
};

const AmenityContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 0 12px;

  &:first-child {
    padding-inline-start: 0;
  }
  &:not(:last-child) {
    border-inline-end: 1px solid #d1d4d9;
  }

  .value {
    color: #1a2028;
    font-size: 14px;
    font-family: materialMedium;
  }

  .label {
    color: #5b6573;
    font-size: 12px;
    font-family: materialRegular;
  }

  @media (max-width: ${props => props.theme.breakPoints.mobile.max}) {
    .label {
      font-size: 11px;
    }
  }
`;

const Container = styled.div`
  display: flex;
  align-items: center;
`;

export default CardAmenities;
